/**
 * Photo handling. A 4 MB phone photo has to land under ~50 KB, so we resize
 * on a canvas before it goes anywhere. SPEC.md §8.
 */

const MAX_W = 320;
const MAX_H = 384;
const QUALITY = 0.78;

/**
 * A payment QR is the one image here that has to survive being READ by a
 * camera, not just looked at. So it gets more pixels and far less JPEG
 * compression than a photo — squeeze a QR to 0.72 and the artefacts around
 * the modules can cost a scan, which on a payment page means the money never
 * moves.
 */
export const QR_IMAGE = { maxW: 900, maxH: 900, quality: 0.92 };

/**
 * A payment screenshot. Tall (a phone screen), and the committee has to READ
 * it — the UTR line, the amount — so it keeps more pixels than a photo but
 * still lands well under the API's 700 KB ceiling.
 */
export const PROOF_IMAGE = { maxW: 1080, maxH: 1600, quality: 0.8 };

/**
 * Event banners are the one image shown full-bleed, so they get the most
 * room — but still land well under the 600 KB Storage rule ceiling. 16:9-ish;
 * the card and the detail hero both crop with object-cover.
 */
export const EVENT_IMAGE = { maxW: 1280, maxH: 800, quality: 0.72 };

/**
 * Opens the file picker, resizes the chosen image, and hands back a JPEG
 * data URL. Rejects with a message fit to show in a toast.
 *
 * Defaults to profile-photo dimensions; pass `EVENT_IMAGE` or `QR_IMAGE`.
 */
export function pickAndResizePhoto(opts = {}) {
  const maxW = opts.maxW ?? MAX_W;
  const maxH = opts.maxH ?? MAX_H;
  const quality = opts.quality ?? QUALITY;

  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';

    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (!file) return; // cancelled — leave the promise pending, nothing happened
      if (!file.type.startsWith('image/')) {
        reject(new Error('Choose an image file'));
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const scale = Math.min(maxW / img.width, maxH / img.height, 1);
          const w = Math.round(img.width * scale);
          const h = Math.round(img.height * scale);

          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not process that image'));
            return;
          }
          ctx.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL("image/jpeg", quality));
        };
        img.onerror = () => reject(new Error('Could not read that image'));
        img.src = reader.result;
      };
      reader.onerror = () => reject(new Error('Could not read that file'));
      reader.readAsDataURL(file);
    });

    input.click();
  });
}
